import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2, Plus, UserRound, X } from "lucide-react";
import { listCoachings, addCoaching, updateCoaching, addCoachingSession, getCoachingSessions } from "../../services/wisdomServices/growthService";
import { getAgentProjectsRequest } from "../../services/wisdomServices/projectService";
import { getSessionItem } from "../../utils/helper/session";
import styles from "./styles.module.css";
import UserShortProfile from "../userShortProfile/UserShortProfile";
import { getUserInformations } from "../../services/wisdomServices/userService";
import { requestGetImagesFromBucket } from "../../services/wisdomServices/trackingService.js";
import DefaultProfileImage from "../../medias/images/default.jpg"
import { focusAreas } from "../../utils/helper/focusAreas.js";
import { format } from "date-fns";

const MAX_SESSION_DISCUSSION_LENGTH = 100
const MAX_SESSION_ITEMS_LENGTH = 255

const INITIAL_COACHING = {
    title: "",
    description: "",
    projectId: "",
    focusAreaId: "",
    followupDate: "",
    status: "new",
};

const formatDate = (date) => {
    if (!date) return "";
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    }).format(parsedDate);
};

const getInitials = (name = "") => {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

const formatStatus = (status = "") => {
    return status
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const getProjectName = (project) => {
    return project?.name || project?.projectName || `Project ${project?.id}`;
};

function UserCoachingsPage() {
    const { employeeId } = useParams();
    const loggedUser = getSessionItem("User");

    const [coachingItems, setCoachingItems] = useState([]);
    const [expandedItems, setExpandedItems] = useState([]);
    const [sessionForms, setSessionForms] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [newCoaching, setNewCoaching] = useState(INITIAL_COACHING);
    const [loggingSessionId, setLoggingSessionId] = useState(null);
    const [loadingSessions, setLoadingSessions] = useState({});
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);

    useEffect(() => {
        if (!employeeId) return;
        getUser();
        loadCoachings();
        loadUserProjects();
    }, [employeeId]);

    const getUser = async () => {
        try {
            
            setLoadingUser(true);
            let response = await getUserInformations(employeeId);
            if (!response.image) {
              response.image = DefaultProfileImage;
            } else {
              const blob = await requestGetImagesFromBucket(
                response.id,
                response.image,
                "profile"
              );
        
              response.image = blob
                ? URL.createObjectURL(blob)
                : DefaultProfileImage;
            }
        
            setUser(response);
        } catch (error) {
            console.error("Something went wrong: ", error)
        } finally {
            setLoadingUser(false)
        }
    };

    const loadCoachings = async () => {
        try {
            setLoading(true);

            const response = await listCoachings({
                coacheeId: Number(employeeId),
                page: 1,
                limit: 50,
            });

            const items = (response?.data || []).map((item) => ({
                ...item,
                sessions: item.sessions || [],
            }));

            setCoachingItems(items);
        } catch (error) {
            console.error("Error loading user coachings:", error);
            console.error("API response:", error?.response?.data);
            setCoachingItems([]);
        } finally {
            setLoading(false);
        }
    };

    const loadUserProjects = async () => {
        if (!employeeId) return;

        try {
            setLoadingProjects(true);

            const response = await getAgentProjectsRequest(employeeId, false, true, 1, 100);

            const projectList = (response?.data || [])
                .map((project) => ({
                    ...project,
                    name: getProjectName(project),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            setProjects(projectList);
        } catch (error) {
            console.error("Error loading employee projects:", error);
            setProjects([]);
        } finally {
            setLoadingProjects(false);
        }
    };

    const loadCoachingSessions = async (coachingId) => {
        try {
            setLoadingSessions((previous) => ({ ...previous, [coachingId]: true }));

            const response = await getCoachingSessions(coachingId, 1, 50);

            setCoachingItems((previous) =>
                previous.map((item) =>
                    item.id === coachingId ? { ...item, sessions: response?.data || [] } : item
                )
            );
        } catch (error) {
            console.error("Error loading coaching sessions:", error);
            console.error("API response:", error?.response?.data);
        } finally {
            setLoadingSessions((previous) => ({ ...previous, [coachingId]: false }));
        }
    };

    const activeCount = useMemo(
        () => coachingItems.filter((item) => item.status !== "closed").length,
        [coachingItems]
    );

    const closedCount = useMemo(
        () => coachingItems.filter((item) => item.status === "closed").length,
        [coachingItems]
    );

    const toggleItem = async (itemId) => {
        const isCurrentlyExpanded = expandedItems.includes(itemId);

        setExpandedItems((previous) =>
            isCurrentlyExpanded ? previous.filter((id) => id !== itemId) : [...previous, itemId]
        );

        if (!isCurrentlyExpanded) await loadCoachingSessions(itemId);
    };

    const handleStatusChange = async (item, newStatus) => {
        if (newStatus === item.status) return;

        try {
            setUpdatingId(item.id);

            const isReopening = item.status === "closed" && newStatus !== "closed";

            const payload = isReopening
                ? { status: newStatus }
                : {
                      coachId: item.coachId,
                      focusAreaId: item.focusAreaId,
                      title: item.title,
                      description: item.description,
                      followupDate: item.followupDate,
                      status: newStatus,
                  };

            await updateCoaching(item.id, payload);

            setCoachingItems((previous) =>
                previous.map((coaching) =>
                    coaching.id === item.id ? { ...coaching, status: newStatus } : coaching
                )
            );
        } catch (error) {
            console.error("Error updating coaching status:", error);
            console.error("API response:", error?.response?.data);
        } finally {
            setUpdatingId(null);
        }
    };

    const updateSessionField = (itemId, field, value) => {
        setSessionForms((previous) => ({
            ...previous,
            [itemId]: {
                ...previous[itemId],
                [field]: value,
            },
        }));
    };

    const handleLogSession = async (itemId) => {
        const form = sessionForms[itemId] || {};
        const discussion = form.discussion?.trim();
        const actionItems = form.actionItems?.trim();

        if (!discussion) return;

        const payload = {
            discussion,
            actionItems: actionItems || "",
            status: "new",
        };

        try {
            setLoggingSessionId(itemId);

            await addCoachingSession(itemId, payload);

            setSessionForms((previous) => ({
                ...previous,
                [itemId]: {
                    discussion: "",
                    actionItems: "",
                },
            }));

            await loadCoachingSessions(itemId);
        } catch (error) {
            console.error("Error creating coaching session:", error);
            console.error("API response:", error?.response?.data);
        } finally {
            setLoggingSessionId(null);
        }
    };

    const handleCreateCoaching = async (event) => {
        event.preventDefault();

        if (
            !newCoaching.title.trim() ||
            !newCoaching.description.trim() ||
            !newCoaching.projectId ||
            !newCoaching.focusAreaId
        ) {
            console.error("Please fill all required fields.");
            return;
        }

        try {
            setCreating(true);

            const payload = {
                coacheeId: Number(employeeId),
                coachId: Number(loggedUser.id),
                projectId: Number(newCoaching.projectId),
                focusAreaId: Number(newCoaching.focusAreaId),
                title: newCoaching.title.trim(),
                description: newCoaching.description.trim(),
                followupDate: newCoaching.followupDate
                    ? new Date(newCoaching.followupDate).toISOString()
                    : null,
                status: "new",
            };

            await addCoaching(payload);
            await loadCoachings();

            setNewCoaching(INITIAL_COACHING);
            setShowCreateModal(false);
        } catch (error) {
            console.error("Error creating coaching:", error);
            console.error("API response:", error?.response?.data);
        } finally {
            setCreating(false);
        }
    };

    const updateNewCoachingField = (field, value) => {
        setNewCoaching((previous) => ({ ...previous, [field]: value }));
    };

    return (
        <div className={styles.page}>
            {!loadingUser && (
                <div className="my-dashboard-header">
                    <UserShortProfile
                        employeeName={user.firstName + ' ' + user.lastName}
                        employeePronoun={user.pronoun}
                        employeeDesignation={user.jobDescription}
                        employeeID={user.id}
                        employeeUsername={user.username}
                        employeeLocation={user.city}
                        employeePicture={user.image}
                        employeeDisabled={user.isDisabled}
                        employeeContractEndDate={user.contractEndDate}
                        employeeDepartment={user.department}
                        employeeIdvStatus={user.trolleyIdvStatus}
                        employeePersonalEmail={user.personalEmail}
                        showManagerLinks={true}
                    />
                </div>
            )}
            <div className={styles.pageContent}>
                <div className={styles.pageHeader}>
                    <div>
                        <div className={styles.titleRow}>
                            <h1>Coaching</h1>
                            <span className={styles.totalBadge}>{coachingItems.length} items</span>
                        </div>

                        <p className={styles.pageDescription}>
                            Manage coaching items, track progress and log coaching sessions.
                        </p>
                    </div>

                    <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>
                        <Plus size={17} />
                        Add coaching item
                    </button>
                </div>

                <div className={styles.summary}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{activeCount}</span>
                        <span className={styles.summaryLabel}>Active</span>
                    </div>

                    <div className={styles.summaryDivider} />

                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{closedCount}</span>
                        <span className={styles.summaryLabel}>Closed</span>
                    </div>
                </div>

                <div className={styles.coachingList}>
                    {loading ? (
                        <div className={styles.emptySessions}>
                            <Loader2 size={20} className={styles.spinner} />
                            Loading coachings...
                        </div>
                    ) : coachingItems.length === 0 ? (
                        <div className={styles.emptySessions}>No coaching items found.</div>
                    ) : (
                        coachingItems.map((item) => {
                            const isExpanded = expandedItems.includes(item.id);
                            const isClosed = item.status === "closed";
                            const isUpdating = updatingId === item.id;
                            const form = sessionForms[item.id] || { discussion: "", actionItems: "" };

                            return (
                                <article key={item.id} className={styles.coachingCard}>
                                    <div
                                        className={styles.cardHeader}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleItem(item.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") toggleItem(item.id);
                                        }}
                                    >
                                        <div className={styles.cardHeaderContent}>
                                            <div className={styles.cardTitleLine}>
                                                <h2>{item.title}</h2>

                                                <span className={`${styles.statusBadge} ${isClosed ? styles.completedBadge : styles.progressBadge}`}>
                                                    {formatStatus(item.status)}
                                                </span>

                                                {item.focusAreaName && (
                                                    <span className={styles.categoryBadge}>{item.focusAreaName}</span>
                                                )}
                                            </div>

                                            <p className={styles.itemDescription}>{item.description}</p>

                                            <div className={styles.peopleRow}>
                                                <div className={styles.person}>
                                                    <span className={styles.coachLabel}>
                                                        <UserRound size={14} />
                                                        Coachee:
                                                    </span>

                                                    <span className={`${styles.avatar} ${styles.userAvatar}`}>
                                                        {getInitials(item.coacheeName)}
                                                    </span>

                                                    <span>{item.coacheeName}</span>
                                                </div>

                                                <div className={styles.person}>
                                                    <span className={styles.coachLabel}>
                                                        <UserRound size={14} />
                                                        Coach:
                                                    </span>

                                                    <span className={`${styles.avatar} ${styles.coachAvatar}`}>
                                                        {getInitials(item.coachName)}
                                                    </span>

                                                    <span>{item.coachName}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.headerActions}>
                                            <select
                                                className={styles.statusSelect}
                                                value={item.status}
                                                disabled={isUpdating}
                                                onClick={(event) => event.stopPropagation()}
                                                onChange={(event) => {
                                                    event.stopPropagation();
                                                    handleStatusChange(item, event.target.value);
                                                }}
                                            >
                                                <option value="new">New</option>
                                                <option value="in-progress">In Progress</option>
                                                <option value="closed">Closed</option>
                                            </select>

                                            {isUpdating && <Loader2 size={16} className={styles.spinner} />}

                                            <button
                                                type="button"
                                                className={styles.expandButton}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleItem(item.id);
                                                }}
                                            >
                                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className={styles.cardContent}>
                                            <div className={styles.sessionsHeader}>
                                                <h3>Sessions</h3>
                                                <span>
                                                    {item.sessions?.length || 0}{" "}
                                                    {item.sessions?.length === 1 ? "session" : "sessions"}
                                                </span>
                                            </div>

                                            {loadingSessions[item.id] ? (
                                                <div className={styles.emptySessions}>
                                                    <Loader2 size={18} className={styles.spinner} />
                                                    Loading sessions...
                                                </div>
                                            ) : item.sessions?.length > 0 ? (
                                                <div className={styles.sessionsList}>
                                                    {item.sessions.map((session) => (
                                                        <div key={session.id} className={styles.sessionCard}>
                                                            <div className={styles.sessionTopRow}>
                                                                <span className={styles.sessionDate}>{formatDate(session.createdAt)}</span>
                                                                <span className={styles.sessionStatus}>{formatStatus(session.status)}</span>
                                                            </div>

                                                            <p className={styles.sessionDiscussion}>{session.discussion}</p>

                                                            {session.actionItems && (
                                                                <div className={styles.actionList}>
                                                                    {session.actionItems
                                                                        .split("\n")
                                                                        .map((action) => action.trim())
                                                                        .filter(Boolean)
                                                                        .map((action, index) => (
                                                                            <div key={`${session.id}-${index}`} className={styles.actionItem}>
                                                                                <span>→</span>
                                                                                <span>{action}</span>
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className={styles.emptySessions}>No coaching sessions logged yet.</div>
                                            )}

                                            {!isClosed && (
                                                <div className={styles.sessionForm}>
                                                    <h4>Log a session</h4>

                                                    <textarea
                                                        rows={3}
                                                        placeholder="What did you discuss?"
                                                        maxLength={MAX_SESSION_DISCUSSION_LENGTH}
                                                        value={form.discussion || ""}
                                                        onChange={(event) => updateSessionField(item.id, "discussion", event.target.value)}
                                                    />

                                                    <textarea
                                                        rows={3}
                                                        placeholder="Action items (one per line)"
                                                        maxLength={MAX_SESSION_ITEMS_LENGTH}
                                                        value={form.actionItems || ""}
                                                        onChange={(event) => updateSessionField(item.id, "actionItems", event.target.value)}
                                                    />

                                                    <div className={styles.sessionFormFooter}>
                                                        <span className={styles.formHint}>Add one action item per line.</span>

                                                        <button
                                                            type="button"
                                                            className={styles.logSessionButton}
                                                            disabled={!form.discussion?.trim() || loggingSessionId === item.id}
                                                            onClick={() => handleLogSession(item.id)}
                                                        >
                                                            {loggingSessionId === item.id ? (
                                                                <>
                                                                    <Loader2 size={14} className={styles.spinner} />
                                                                    Logging...
                                                                </>
                                                            ) : (
                                                                "Log session"
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </article>
                            );
                        })
                    )}
                </div>

                {showCreateModal && (
                    <div className={styles.modalOverlay} onMouseDown={() => setShowCreateModal(false)}>
                        <div className={styles.modal} onMouseDown={(event) => event.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <div>
                                    <h2>Add coaching item</h2>
                                    <p>Create a new coaching objective for this team member.</p>
                                </div>

                                <button type="button" className={styles.closeButton} onClick={() => setShowCreateModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>

                            <form className={styles.modalForm} onSubmit={handleCreateCoaching}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="coaching-title">Title</label>
                                    <input
                                        id="coaching-title"
                                        type="text"
                                        placeholder="Ex: Improve written communication"
                                        value={newCoaching.title}
                                        onChange={(event) => updateNewCoachingField("title", event.target.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="coaching-description">Description</label>
                                    <textarea
                                        id="coaching-description"
                                        rows={4}
                                        maxLength={255}
                                        placeholder="Describe the coaching goal and expected improvement..."
                                        value={newCoaching.description}
                                        onChange={(event) => updateNewCoachingField("description", event.target.value)}
                                    />
                                    <span className={styles.formHint}>{newCoaching.description.length}/255</span>
                                </div>

                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="coaching-project">Project</label>

                                        <select
                                            id="coaching-project"
                                            value={newCoaching.projectId}
                                            disabled={loadingProjects}
                                            onChange={(event) => updateNewCoachingField("projectId", event.target.value)}
                                        >
                                            <option value="">
                                                {loadingProjects ? "Loading projects..." : "Select a project"}
                                            </option>

                                            {projects.map((project) => (
                                                <option key={project.id} value={project.id}>
                                                    {project.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="coaching-focus-area">Focus Area</label>
                                        <select value={newCoaching.focusAreaId} id="coaching-focus-area" onChange={(event) => updateNewCoachingField("focusAreaId", event.target.value)}>
                                            {focusAreas.map(item => {
                                                return(
                                                    <option  key={item.id} value={item.id}>{item.name}</option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="coaching-followup">Follow-up date</label>
                                    <input
                                        id="coaching-followup"
                                        type="date"
                                        min={format(new Date(), "yyyy-MM-dd")}
                                        value={newCoaching.followupDate}
                                        onChange={(event) => updateNewCoachingField("followupDate", event.target.value)}
                                    />
                                </div>

                                <div className={styles.modalFooter}>
                                    <button type="button" className={styles.cancelButton} onClick={() => setShowCreateModal(false)}>
                                        Cancel
                                    </button>

                                    <button type="submit" className={styles.createButton} disabled={creating}>
                                        {creating ? <Loader2 size={16} className={styles.spinner} /> : <Plus size={16} />}
                                        {creating ? "Creating..." : "Create coaching item"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserCoachingsPage;